import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Autoplay, Pagination } from "swiper/modules";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import { HERO_IMAGES } from "../../lib/config";

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => {
  const swiperRef = useRef(null);
  const navigate = useNavigate();
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <section
      id="top"
      className="relative w-full overflow-hidden"
      style={{ height: "100svh", minHeight: "640px" }}
      data-testid="hero-section"
    >
      {/* Carousel */}
      <Swiper
        onSwiper={(s) => (swiperRef.current = s)}
        modules={[EffectFade, Autoplay, Pagination]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        loop
        speed={1600}
        autoplay={
          reduced
            ? false
            : { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: false }
        }
        pagination={{ clickable: true }}
        className="absolute inset-0"
        style={{ position: "absolute", inset: 0, height: "100%", width: "100%" }}
        data-testid="hero-swiper"
      >
        {HERO_IMAGES.map((img, i) => (
          <SwiperSlide key={i} className="!h-full !w-full">
            <div className="relative h-full w-full overflow-hidden">
              <img
                src={img.src}
                alt={img.alt}
                loading={i === 0 ? "eager" : "lazy"}
                className={`absolute inset-0 h-full w-full object-cover ${
                  reduced ? "" : "kenburns"
                }`}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Scrims: dark green gradient + subtle vignette */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(115deg, rgba(18,43,32,0.72) 0%, rgba(18,43,32,0.35) 55%, rgba(18,43,32,0.08) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 80% at 8% 90%, rgba(18,43,32,0.65) 0%, rgba(18,43,32,0) 60%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 h-full flex items-end lg:items-center">
        <div className="max-w-7xl mx-auto w-full px-6 md:px-10 pt-32 pb-24 lg:py-32">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.35 }}
            className="eyebrow"
            style={{ color: "#C6A15B" }}
            data-testid="hero-eyebrow"
          >
            Family-Owned Land Acquisition &amp; Development
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.5 }}
            className="font-heading font-medium text-[#FAF7F2] mt-5 max-w-3xl"
            style={{
              fontSize: "clamp(2.6rem, 5.4vw, 4.6rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.015em",
            }}
            data-testid="hero-headline"
          >
            Your House. Your Land.{" "}
            <span className="gold-italic">A Fair Cash Offer.</span>{" "}
            A Simple Close.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.75 }}
            className="mt-7 max-w-2xl text-[#FAF7F2]/90 font-body text-base md:text-lg"
            style={{ lineHeight: 1.7 }}
            data-testid="hero-subhead"
          >
            Ibex Acquisitions &amp; Development buys houses and land as-is,
            for cash — no agents, no commissions, no repairs, no waiting. Tell
            us about your property and get a no-obligation offer within 48
            hours.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 1.0 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={scrollTo("offer-form")}
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              Get My Cash Offer
            </button>
            <button
              onClick={() => navigate("/builders")}
              className="btn-ghost on-dark"
              data-testid="hero-cta-secondary"
            >
              Partner With Us
            </button>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.a
        href="#promise"
        onClick={scrollTo("promise")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#C6A15B]"
        aria-label="Scroll to next section"
        data-testid="hero-scroll-indicator"
      >
        <span
          className="text-[0.6rem]"
          style={{ letterSpacing: "0.32em", textTransform: "uppercase" }}
        >
          Scroll
        </span>
        <div className="w-px h-10 bg-[#C6A15B]/60" />
        <ChevronDown size={18} className="scroll-indicator" strokeWidth={1.5} />
      </motion.a>
    </section>
  );
};

export default Hero;
